import { Component, OnInit, Output, EventEmitter, OnDestroy, ViewChild, TemplateRef, Input } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService, GlobalEventBusService, hd, IDeviceModel } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { RcTabIndexes } from 'app/modules/remote-control/services/rc-tab.service';
import { WorkPanelManagerService, WorkPanelType } from 'app/modules/remote-control/services/work-panel-manager.service';
import {
  IDeviceSnapshotStatus,
  SnapshotService,
  SnapshotSaveStatus,
  DeviceSnapshotSaveRequestStatus,
  snapshotAction,
} from 'app/modules/ticket/services/snapshot.service';
import { Toast, ToastTypeIndex } from 'app/shared/components/toast/toast.component';
import { BroadcastService, BroadcastChannels } from 'app/utils/broadcastService';
import { WorkPanelBarItem } from '../../work-panel/models/work-panel-bar-items';
import { DeviceInfoViewComponent } from './device-info-view/device-info-view.component';
import { ISnapshotInformation } from './snapshot.models';
import { DeviceInfoComponent } from './device-info/device-info.component';
@Component({
  selector: 'xs-snapshot',
  templateUrl: './snapshot.component.html',
  styleUrls: ['./snapshot.component.scss'],
})
export class SnapshotComponent implements OnInit, OnDestroy {
  @Input() isSaveSnapshotEnabled: boolean;
  @Input() isMinimizeButtonVisible: boolean;
  @Input() currentWorkPanelKey: string;
  @Input() isPopInOutExternallyDisabled: boolean;
  @Input() popOut = false;
  @Input() titleHeight: number;
  @Output() closeSnapshotEvent = new EventEmitter<number>();
  @Output() minimizeSnapshotEvent = new EventEmitter<void>();
  @Output() menuBarItems = new EventEmitter<WorkPanelBarItem[]>();
  @Output() actionBarRefEvent = new EventEmitter<TemplateRef<any>>();
  @Output() titleBarRefEvent = new EventEmitter<TemplateRef<any>>(true);
  @Output() setToast = new EventEmitter<Toast>();
  @Output() snapshotRefreshInProgress = new EventEmitter<boolean>();
  @Input() isLicenceActive: boolean;

  @ViewChild(DeviceInfoViewComponent, { static: true }) deviceInfoViewComponent: DeviceInfoViewComponent;
  @ViewChild(DeviceInfoComponent, { static: true }) deviceInfoComponent: DeviceInfoComponent;
  @ViewChild('actionBar', { static: true }) actionBarRef: TemplateRef<any>;
  @ViewChild('titleBar', { static: true }) titleBar: TemplateRef<any>;

  isPopInOutDisabled: boolean;
  snapshotActivity: hd.ISnapshotActivity;
  device: IDeviceModel;
  isLive: boolean;
  mcConnectionName: string;
  isSnapshotRefreshInProgress: boolean;
  lastRefreshedSnapshotDateTime: string;
  snapshotBarHidden = new BehaviorSubject(false);
  isSnapshotBarHidden = false;

  private previewSnapshotSub: Subscription;
  private activityDeletedSub: Subscription;
  private openLiveSnapshotSub: Subscription;
  private readonly deviceSnapshotStatusChanged: BroadcastService<IDeviceSnapshotStatus> = new BroadcastService(
    BroadcastChannels.onDeviceSnapshotStatusChanged
  );

  constructor(
    private globalEventBusService: GlobalEventBusService,
    private featureToggleService: FeatureToggleService,
    private saI18nPipe: XsI18nPipe,
    private snapshotService: SnapshotService,
    private workPanelManagerService: WorkPanelManagerService,
  ) {}

  ngOnInit() {
    this.previewSnapshotSub = this.globalEventBusService.register(
      GlobalEvents.previewSnapshot,
      (result: ISnapshotInformation) => {
        if (result && result.snapshotActivity) {
          this.snapshotActivity = result.snapshotActivity;
          this.device = result.device;
          this.mcConnectionName = this.getConnectionName(result);
          this.isLive = false;
          this.titleBarRefEvent.emit(this.titleBar);
          this.setisPopInOutDisabled();
        } else {
          console.error('Error: Snapshot not found for preview snapshot');
        }
        this.setisPopInOutDisabled();
      }
    );

    this.openLiveSnapshotSub = this.globalEventBusService.register(
      GlobalEvents.openLiveSnapshot,
      (result: ISnapshotInformation) => {
        if (result) {
          this.currentWorkPanelKey = result.workPanelKey;
          this.snapshotActivity = null;
          this.device = result.device;
          this.mcConnectionName = result.mcConnectionName;
          this.isLive = true;
          this.updateLastRefreshedSnapshotDateTime();
          this.actionBarRefEvent.emit(this.actionBarRef);
          this.titleBarRefEvent.emit(this.titleBar);

          // for firefox disable scrolling until current item selected.
          this.deviceInfoViewComponent?.delayScrollTracking();
        } else {
          console.error('Error: ISnapshotInformation missing on opening live snapshot');
        }
      }
    );

    this.activityDeletedSub = this.globalEventBusService.register(
      GlobalEvents.activityDeleted,
      (activity: hd.IDeviceCaptureActivity) => {
        // if the current saved snapshot preview gets deleted, close the work panel
        if (!this.isLive && this.snapshotActivity && this.snapshotActivity.parentActivityId === activity.activityId) {
          this.closeSnapshotEvent.emit(WorkPanelType.savedSnapshot);
        }
      }
    );

    this.menuBarItems.emit(this.getMenubarItems());

    this.deviceSnapshotStatusChanged.onMessage = this.handleDeviceSnapshotStatusChange;
    this.snapshotService.fetchingStatus$.subscribe(isFetching => {
      this.updateLastRefreshedSnapshotDateTime();
    });
  }

  public refreshClicked = (): void => {
    if (this.isLicenceActive) {
      this.deviceInfoViewComponent?.onRefreshSnapshotPressed();
    } else {
      this.deviceInfoComponent?.refreshInfo();
    }
  };

  ngOnDestroy() {
    [this.previewSnapshotSub, this.activityDeletedSub, this.openLiveSnapshotSub]
      .filter(sub => !!sub)
      .forEach(sub => sub.unsubscribe());

    if (this.deviceSnapshotStatusChanged) {
      this.deviceSnapshotStatusChanged.close();
    }
  }


  setisPopInOutDisabled = (): void => {
    if (this.isPopInOutExternallyDisabled) {
      this.isPopInOutDisabled = true;
    }

    if (!this.device) {
      this.isPopInOutDisabled = false;
    }

    const snapshotState = this.snapshotService.getSnapshotMetaData(this.device.deviceId);
    this.isPopInOutDisabled = !!snapshotState && snapshotState.saveStatus === SnapshotSaveStatus.Saving;
  };

  closeSnapshot = () => {
    // HD-11194 temporary fix only for 1.8
    // The scrollToTop should be removed once the currentPage functionality has been properly implemented (current target 2.0)
    this.deviceInfoViewComponent.scrollToTop();
    setTimeout(() =>
      this.closeSnapshotEvent.emit(this.isLive ? WorkPanelType.liveSnapshot : WorkPanelType.savedSnapshot)
    );
  };

  minimizeSnapshot = () => {
    this.minimizeSnapshotEvent.emit();
  };

  getCreationDate = (): string => {
    if (this.isLive) {
      return this.lastRefreshedSnapshotDateTime;
    } else {
      return this.snapshotActivity?.creationDate ?? '';
    }
  };

  onSnapshotRefreshInProgressChanged(inProgress: boolean) {
    this.isSnapshotRefreshInProgress = inProgress;
    this.snapshotRefreshInProgress.emit(inProgress);
  }

  /**
    Determine if the snapshot bar should be visible
    If all the buttons of the bar are hidden than
    the bar should be hidden
   */
  setSnapshotBarVisible(isAnyButtonVisible: boolean) {
    this.snapshotBarHidden.next(!isAnyButtonVisible);
    this.isSnapshotBarHidden = !isAnyButtonVisible;
  }

  getConnectionName = (snapShotInfo: ISnapshotInformation): string => {
    if (snapShotInfo?.mcConnectionName) {
      return snapShotInfo.mcConnectionName;
    }

    return this.saI18nPipe.transform(
      snapShotInfo?.isDefaultConnection
        ? 'associateDevices_Default_Instance_Caption'
        : 'associateDevices_Unknown_Instance_Caption'
    );
  };

  isTicketPage = (): boolean => {
    return document.getElementById('ticketSection') != null;
  };

  private handleDeviceSnapshotStatusChange = (status: IDeviceSnapshotStatus) => {
    if (this.isLive) {
      this.updateLastRefreshedSnapshotDateTime();
    }

    if (this.featureToggleService.isEnabled('DeveloperMode')) {
      console.log('[SnapshotComponent] DeviceSnapshotSaveStatus: ', status);
    }

    if (status.status === DeviceSnapshotSaveRequestStatus.SaveErrored) {
      this.setToast.emit({
        message: this.saI18nPipe.transform('snapshot_ErrorSavingDeviceSnapshot'),
        type: ToastTypeIndex.error,
      });

      // We already processed the error, we can now set the status to None
      this.deviceSnapshotStatusChanged.postMessage({
        action: snapshotAction.Save,
        activityId: status.activityId,
        status: DeviceSnapshotSaveRequestStatus.None,
      } as IDeviceSnapshotStatus);
    }
    this.setisPopInOutDisabled();
  };

  private updateLastRefreshedSnapshotDateTime = () => {
    if (this.isLive && this.device && this.device.deviceId) {
      const refreshedDateTime = this.snapshotService.getSnapshotLastRefreshed(this.device.deviceId);
      if (refreshedDateTime) {
        this.lastRefreshedSnapshotDateTime = refreshedDateTime.toString();
      }
    }
  };

  /** used when user clicks snapshot on the menu bar  */
  private snapshotClickedOnMenuBar = () => {
    if (!this.isCurrentWorkPanelLiveSnapshot()) {
      // get the device currently being viewed in the work panel (rc, file explorer, task manager etc)
      const currentWorkPanelDevice = this.workPanelManagerService.getPanelDevice(this.currentWorkPanelKey);
      if (!currentWorkPanelDevice) {
        console.error('Snapshot clicked but no device specified');
        return;
      }

      const snapshotInfo: ISnapshotInformation = {
        device: currentWorkPanelDevice,
        mcConnectionName: currentWorkPanelDevice.$$mcConnectionName,
        isDefaultConnection: currentWorkPanelDevice.$$isDefaultMcConnection,
        snapshotActivity: null,
        workPanelKey: this.currentWorkPanelKey,
      };
      this.globalEventBusService.broadcast(GlobalEvents.openLiveSnapshot, snapshotInfo);
    }
  };

  private isCurrentWorkPanelLiveSnapshot = (): boolean => {
    return this.workPanelManagerService.getPanelType(this.currentWorkPanelKey) === WorkPanelType.liveSnapshot;
  };

  private isSnapshotAvailable = (): boolean => {
    const currentWorkPanelDevice = this.workPanelManagerService.getPanelDevice(this.currentWorkPanelKey);
    if (currentWorkPanelDevice) {
      return this.snapshotService.hasSnapshotPermissions(currentWorkPanelDevice);
    }
    return false;
  };

  private getTitle = (): string => {
    if (!this.isLicenceActive) {
      return this.saI18nPipe.transform('ticket_RemoteControlTabs_DeviceInfo');
    }

    return this.saI18nPipe.transform('ticket_RemoteControlTabs_DeviceSnapshot');
  };

  private getMenubarItems = (): WorkPanelBarItem[] => [
    {
      name: 'devicesnapshot',
      tooltip: this.getTitle,
      onClick: this.snapshotClickedOnMenuBar,
      icon: () => 'hd-icon-deviceinfo',
      isVisible: this.isSnapshotAvailable,
      tabIndex: RcTabIndexes.snapshot,
      selected: this.isCurrentWorkPanelLiveSnapshot,
      isDisabled: () => false,
    },
  ];
}
